<template>
  <page-template :is-loading="isLoading" :data-exists="challenge !== null">
    <template #pageHeadline>
      {{ $t('interactiveChallenge.pageHeadline') }}
    </template>
    <template #secondLevelHeaderRight>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              :items="[
                ['ltr', $t('interactiveChallenge.direction.leftToRight')],
                ['rtl', $t('interactiveChallenge.direction.rightToLeft')],
              ]"
              v-model:value="challenge.attrs.html.dir"
              :label="$t('interactiveChallenge.direction.label')"
              class="direction-selection"
              data-qa="switch-direction-dropdown"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              :items="[
                ['logo', $t('interactiveChallenge.style.logo')],
                ['wallpaper', $t('interactiveChallenge.style.wallpaper')],
              ]"
              v-model:value="challenge.position.style"
              :label="$t('interactiveChallenge.style.label')"
              class="style-selection"
              data-qa="switch-style-dropdown"
            />
          </div>
          <div class="rbz-field">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('interactiveChallenge.palette.label') }}
            </label>
            <div class="palette-color-picker">
              <label
                class="rbz-label rbz-label-inline width-100px"
                :class="{ disabled: planetStore.isUpdateInProcess }"
                for="palette-first"
              >
                {{ $t('interactiveChallenge.palette.firstColor') }}
              </label>
              <input
                v-model="challenge.palette[0]"
                class="palette-color-input width-100px"
                :disabled="planetStore.isUpdateInProcess"
                type="color"
                id="palette-first"
                tabindex="0"
                data-qa="palette-first-input"
                placeholder="Palette first color"
              />
            </div>
            <div class="palette-color-picker">
              <label
                class="rbz-label rbz-label-inline width-100px"
                :class="{ disabled: planetStore.isUpdateInProcess }"
                for="palette-second"
              >
                {{ $t('interactiveChallenge.palette.secondColor') }}
              </label>
              <input
                v-model="challenge.palette[1]"
                class="palette-color-input width-100px"
                :disabled="planetStore.isUpdateInProcess"
                type="color"
                id="palette-second"
                tabindex="0"
                data-qa="palette-second-input"
                placeholder="Palette second color"
              />
            </div>
            <div class="palette-color-picker">
              <label
                class="rbz-label rbz-label-inline width-100px"
                :class="{ disabled: planetStore.isUpdateInProcess }"
                for="palette-third"
              >
                {{ $t('interactiveChallenge.palette.thirdColor') }}
              </label>
              <input
                v-model="challenge.palette[2]"
                class="palette-color-input width-100px"
                :disabled="planetStore.isUpdateInProcess"
                type="color"
                id="palette-third"
                tabindex="0"
                data-qa="palette-third-input"
                placeholder="Palette third color"
              />
            </div>
            <div class="palette-color-picker">
              <label
                class="rbz-label rbz-label-inline width-100px"
                :class="{ disabled: planetStore.isUpdateInProcess }"
                for="palette-fourth"
              >
                {{ $t('interactiveChallenge.palette.fourthColor') }}
              </label>
              <input
                v-model="challenge.palette[3]"
                class="palette-color-input width-100px"
                :disabled="planetStore.isUpdateInProcess"
                type="color"
                id="palette-fourth"
                tabindex="0"
                data-qa="palette-fourth-input"
                placeholder="Palette fourth color"
              />
            </div>
          </div>
        </div>
        <div class="rbz-column is-8">
          <div class="rbz-field">
            <label
              class="rbz-label rbz-label-list"
              :class="{ disabled: planetStore.isUpdateInProcess }"
            >
              {{ $t('interactiveChallenge.languages.label') }}
            </label>
            <rbz-card-collapsible
              v-for="(languageValue, languageKey, index) in challenge.lang"
              :key="languageKey"
              class="language-card"
              :data-qa="`language-card-${languageKey}`"
              :ref="(el) => (languageCardsRefs[index] = el)"
              :is-collapsed="false"
            >
              <template #title>
                {{ languageKeyToFullName(languageKey) }} ({{ languageKey }})
              </template>
              <template #content>
                <div class="rbz-field">
                  <rbz-form-input
                    name="language-title-input"
                    data-qa="language-title-input"
                    classes="language-title-input"
                    :label="$t('interactiveChallenge.languages.card.title')"
                    :placeholder="$t('interactiveChallenge.languages.card.title')"
                    v-model:inputValue="languageValue.title"
                  />
                </div>
                <div class="rbz-field" :class="{ 'is-active': buttonsTextFieldsActive }">
                  <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
                    {{ $t('interactiveChallenge.languages.card.buttonsText') }}
                  </label>
                  <rbz-form-input
                    name="language-first-click-text-input"
                    data-qa="language-first-click-text-input"
                    :placeholder="
                      $t('interactiveChallenge.languages.card.buttonsTextFirstInputPlaceholder')
                    "
                    class="click-text-input"
                    classes="language-first-click-text-input"
                    @focus="buttonsTextFieldsActive = true"
                    @blur="buttonsTextFieldsActive = false"
                    v-model:inputValue="languageValue.click['0']"
                  />
                  <rbz-form-input
                    name="language-second-click-text-input"
                    data-qa="language-second-click-text-input"
                    :placeholder="
                      $t('interactiveChallenge.languages.card.buttonsTextSecondInputPlaceholder')
                    "
                    class="click-text-input"
                    classes="language-second-click-text-input"
                    @focus="buttonsTextFieldsActive = true"
                    @blur="buttonsTextFieldsActive = false"
                    v-model:inputValue="languageValue.click['1']"
                  />
                </div>
                <div class="rbz-field">
                  <rbz-form-textarea
                    name="image-element-source-input"
                    data-qa="image-element-source-textarea"
                    classes="image-element-source-input"
                    :label="$t('interactiveChallenge.languages.card.imageElementSource')"
                    :placeholder="$t('interactiveChallenge.languages.card.imageElementSource')"
                    v-model:inputValue="languageValue.src"
                    rows="4"
                  >
                  </rbz-form-textarea>
                </div>
                <div class="rbz-field" v-if="languageValue.src">
                  <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
                    {{ $t('interactiveChallenge.languages.card.imageElementPreview') }}
                  </label>
                  <div class="img-preview">
                    <img
                      :src="languageValue.src"
                      :alt="
                        $t('interactiveChallenge.languages.card.imageElementCouldNotLoadMessage')
                      "
                    />
                  </div>
                </div>
                <rbz-form-button
                  :label="$t('interactiveChallenge.languages.card.remove')"
                  :disabled="languageKey === 'en' || Object.keys(challenge.lang).length === 1"
                  variant="tertiary"
                  icon="IconDelete"
                  class="remove-language-button"
                  data-qa="remove-language-button"
                  @button-click="removeLanguage(languageKey)"
                />
              </template>
            </rbz-card-collapsible>
            <rbz-form-button
              v-if="!newLanguageEntryOpen"
              :label="$t('interactiveChallenge.languages.newLanguage')"
              variant="secondary"
              class="add-another-language-button"
              data-qa="add-another-language-button"
              @button-click="setNewLanguageEntryOpen(true)"
            />
            <div v-if="newLanguageEntryOpen" class="new-language-entry-wrapper">
              <label class="rbz-label">
                {{ $t('interactiveChallenge.languages.newLanguage') }}
              </label>
              <div class="width-250px">
                <rbz-dropdown
                  :items="languages.map((item) => [item.code, item.name])"
                  v-model:value="newLanguageKey"
                  class="new-language-selection"
                  data-qa="new-language-dropdown"
                  :is-invalid="isNewLanguageKeyInvalid"
                  ref="newLanguageKeyDropdown"
                  error-message-text="Language already exists"
                />
              </div>
              <form-confirmation
                @confirm="addLanguage()"
                @cancel="setNewLanguageEntryOpen(false)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </page-template>
</template>

<script lang="ts">
import RequestsUtils from '@/helpers/RequestsUtils';
import { useRouting } from '@/helpers/RoutingUtils';
import { defineComponent, nextTick } from 'vue';
import { Challenge, DocumentName } from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import languages from '@/assets/languages.json';
import _ from 'lodash';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { usePlanetStore } from '@/store/PlanetStore';

export default defineComponent({
  name: 'InteractiveChallenge',
  setup() {
    const { normalizeRouteName } = useRouting();
    return { normalizeRouteName };
  },
  components: {
    FormConfirmation,
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    PageTemplate,
    RbzCardCollapsible,
    RbzDropdown,
  },
  data() {
    return {
      planet: null,
      challenge: null as Challenge,
      isLoading: true,
      languages: languages,
      newLanguageEntryOpen: false,
      newLanguageKey: null,
      buttonsTextFieldsActive: false,
      languageCardsRefs: [],
    };
  },
  watch: {
    selectedBranch: {
      handler: async function () {
        if (this.normalizeRouteName(this.$route.name as string) === 'Interactive Challenge') {
          await this.loadChallenge();
        }
      },
      immediate: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('interactiveChallenge.plural'),
        singular: this.$t('interactiveChallenge.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isNewLanguageKeyInvalid(): boolean {
      return Object.keys(this.challenge.lang).includes(this.newLanguageKey);
    },
  },
  methods: {
    async loadChallenge() {
      this.isLoading = true;
      this.challenge = null;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/planets`,
        onFail: () => {
          console.log(`Error while attempting to load the planet data`);
          this.challenge = null;
        },
      });
      this.planet = response?.data || {};
      this.challenge = _.cloneDeep(this.planet.ichallenge) || {};
      this.normalizeChallengeData();
      this.isLoading = false;
    },

    async saveChanges() {
      this.isLoading = true;
      this.planet.ichallenge = _.cloneDeep(this.challenge);
      const successMessage = this.$t('common.operations.save.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'PUT',
        url: `conf/${this.selectedBranch}/planets/${this.planet.id}`,
        data: this.planet,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      this.isLoading = false;
    },

    setNewLanguageEntryOpen(value: boolean) {
      this.newLanguageKey = null;
      this.newLanguageEntryOpen = value;
      if (value) {
        nextTick(() => {
          this.$refs.newLanguageKeyDropdown?.focusDropdown();
        });
      }
    },

    addLanguage() {
      if (!this.newLanguageKey || this.isNewLanguageKeyInvalid) {
        return;
      }
      this.challenge.lang[this.newLanguageKey] = {
        click: {
          '0': 'Click here',
          '1': 'Click again',
        },
        title: 'Human verification in process...',
        src: '',
      };
      nextTick(() => {
        this.languageCardsRefs[this.languageCardsRefs.length - 1].$el
          .querySelectorAll('.rbz-card-header')[0]
          .focus();
      });
      this.setNewLanguageEntryOpen(false);
    },

    removeLanguage(key: string) {
      if (Object.keys(this.challenge.lang).length > 1) {
        delete this.challenge.lang[key];
      }
    },

    languageKeyToFullName(key: string) {
      const language = this.languages.find((language) => {
        return language.code === key;
      });
      return language.name || '';
    },

    normalizeChallengeData() {
      const basicChallenge: Challenge = {
        attrs: {
          html: {
            dir: 'ltr',
          },
        },
        palette: [] as string[],
        position: {
          style: 'logo',
        },
        lang: {},
      };
      this.challenge = {
        ...basicChallenge,
        ...this.challenge,
      };
      this.challenge.palette[0] = this.challenge.palette[0] || '#4285f4';
      this.challenge.palette[1] = this.challenge.palette[1] || '#fbbc05';
      this.challenge.palette[2] = this.challenge.palette[2] || '#34a853';
      this.challenge.palette[3] = this.challenge.palette[3] || '#f5d57a';
    },
  },
});
</script>

<style scoped lang="scss">
.palette-color-picker {
  align-items: center;
  display: flex;
  margin-bottom: 8px;
  margin-left: 16px;
}

.palette-color-input {
  cursor: pointer;
}

.click-text-input:not(:last-child) {
  margin-bottom: 8px;
}

.img-preview {
  border: 1px solid;
  height: fit-content;
  max-height: 500px;
  max-width: 500px;
  padding: 8px;
  width: fit-content;
}

.new-language-entry-wrapper {
  align-items: center;
  display: flex;
  gap: 16px;
}
</style>
